import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { getErrorMessage } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Mail } from "lucide-react";

export default function VerifyEmailChange() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("");
  
  const token = searchParams.get("token");

  useEffect(() => {
    const verifyEmailChange = async () => {
      if (!token) {
        setStatus("error");
        setMessage("No verification token provided");
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-email-change?token=${token}`);
        setStatus("success");
        setMessage(response.data.message || "Email address verified and updated successfully.");
        toast.success("Email updated successfully!");
      } catch (error) {
        setStatus("error");
        const errorMessage = getErrorMessage(error.response?.data?.detail, "Verification failed");
        setMessage(errorMessage);
        toast.error(errorMessage);
      }
    };

    verifyEmailChange();
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -top-1/2 -left-1/2 w-full h-full rounded-full blur-3xl transition-colors ${
          status === 'success' ? 'bg-gradient-to-br from-green-500/10 via-transparent to-transparent' :
          status === 'error' ? 'bg-gradient-to-br from-red-500/10 via-transparent to-transparent' :
          'bg-gradient-to-br from-primary/10 via-transparent to-transparent'
        }`} />
      </div>

      <Card className="w-full max-w-md glass-heavy animate-fade-in relative z-10">
        <CardHeader className="space-y-4 text-center">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
            status === 'success' ? 'bg-green-500/20' :
            status === 'error' ? 'bg-red-500/20' :
            'bg-primary/20'
          }`}>
            {status === 'verifying' && <Loader2 className="h-8 w-8 text-primary animate-spin" />}
            {status === 'success' && <CheckCircle2 className="h-8 w-8 text-green-500" />}
            {status === 'error' && <XCircle className="h-8 w-8 text-red-500" />}
          </div>
          <CardTitle className="text-xl">
            {status === 'verifying' && 'Verifying email...'}
            {status === 'success' && 'Email Updated'}
            {status === 'error' && 'Verification Failed'}
          </CardTitle>
          <CardDescription className="text-sm">
            {message || (status === 'verifying' ? 'Please wait while we verify your new email address.' : '')}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'success' && (
            <>
              <p className="text-sm text-muted-foreground">
                Your primary contact email has been updated. You can view the change on your profile.
              </p>
              <Button onClick={() => navigate("/profile")} className="w-full">
                Go to Profile
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <p className="text-sm text-muted-foreground">
                The verification link may have expired or already been used.
              </p>
              <Button onClick={() => navigate("/profile")} variant="outline" className="w-full">
                Go to Profile
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
